import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonContent,
  IonPage,
} from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar } from '../components/ui/TopBar';
import { useCollapsingTitle } from '../hooks/useCollapsingTitle';
import { PullToRefresh } from '../components/ui/PullToRefresh';
import { ConnectFriendsModal } from '../components/profile/ConnectFriendsModal';
import { ConnectionsListModal } from '../components/profile/ConnectionsListModal';
import { CountBadge } from '../components/ui/CountBadge';
import { ShareLevelPicker } from '../components/profile/ShareLevelPicker';
import { useProfileStore } from '../store/useProfileStore';
import {
  useActiveProfile,
  useUpdateShareConnectionLevel,
  useUpdateSeeConnectionLevel,
} from '../hooks/useProfile';
import { useMyConnections } from '../hooks/useConnections';
import { useLocationStore } from '../store/useLocationStore';
import { isChef } from '../types/profile';

// Connect-with-Friends feature flag. Flip to `true` to re-enable. The
// Connections section card + incoming/sent request lists + the Connect
// Friends modal all stay wired; this just hides their render. The feature
// needs more testing/polish before going live. See also HomePage.
const SHOW_CONNECTIONS_FEATURE = true;

/**
 * Connections & Visibility. Holds the connections surface (view connections +
 * connect with friends) and the chef-only "Who can see your dishes" reach
 * picker — both split off the old single ProfilePage.
 */
export const ConnectionsVisibilityPage: React.FC = () => {
  const { t } = useTranslation(['profile', 'connections']);
  // Refetch the active profile on mount so the reach picker reflects any
  // BE-side change the FE didn't initiate.
  useActiveProfile();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const queryClient = useQueryClient();
  const chef = isChef(activeProfile);
  const updateReach = useUpdateShareConnectionLevel();
  // Inbound feed preference ("whose listings I can see") — applies to every
  // user, not just chefs, and isn't constrained by the locale reach cap.
  const updateSeeReach = useUpdateSeeConnectionLevel();
  // Locale cap fed to the Reach picker. The chef's profile-level default
  // applies wherever they list, but we constrain the visible options to the
  // cap of their current dish locale — that's the locale most chefs publish
  // into. The BE still server-side-clamps via LEAST() as a backstop.
  const localeMaxShareConnectionLevel = useLocationStore(
    (s) => s.locale?.maxShareConnectionLevel,
  );
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);
  // Incoming requests waiting on the user — used to badge the View
  // Connections button so a pending request is visible without opening the
  // sheet. Only fetched while the feature is on.
  const { data: incomingRequests } = useMyConnections('requests_to_me', {
    enabled: SHOW_CONNECTIONS_FEATURE,
  });
  const incomingRequestCount = incomingRequests?.length ?? 0;

  const contentRef = useRef<HTMLIonContentElement>(null);
  // Stable identity via useCallback — PullToRefresh rebinds its gesture
  // whenever `onRefresh` changes.
  const handleRefresh = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['profile'] }),
        queryClient.invalidateQueries({ queryKey: ['connections'] }),
      ]),
    [queryClient],
  );

  // Title band hides on scroll-down, returns on scroll-up.
  const { collapsed, onScroll } = useCollapsingTitle(contentRef);

  return (
    <IonPage>
      <TopBar title={t('profile:menu.connections')} titleCollapsed={collapsed} />
      <IonContent ref={contentRef} scrollEvents onIonScroll={onScroll}>
        <PullToRefresh contentRef={contentRef} onRefresh={handleRefresh}>
        <div className="mx-auto max-w-lg px-4 pt-4 pb-2 space-y-6">
          {/* Connections feature surface — gated on SHOW_CONNECTIONS_FEATURE. */}
          {SHOW_CONNECTIONS_FEATURE && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <h3 className="font-medium">{t('connections:section.title')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('connections:section.description')}
              </p>
              {/* Equal-height rows so that when one button's label wraps to
                  two lines at large text sizes, the other grows to match
                  instead of looking lopsided. */}
              <div className="grid auto-rows-fr gap-3">
                {/* Relative wrapper so the badge can sit on the button's
                    upper-right corner without affecting the button's layout. */}
                <div className="relative">
                  <IonButton
                    expand="block"
                    fill="outline"
                    className="h-full"
                    onClick={() => setShowConnectionsModal(true)}
                  >
                    {t('connections:list.viewCta')}
                  </IonButton>
                  <CountBadge
                    count={incomingRequestCount}
                    className="-right-1.5 -top-1.5 z-10"
                  />
                </div>
                <IonButton
                  expand="block"
                  fill="outline"
                  className="h-full"
                  onClick={() => setShowConnectModal(true)}
                >
                  {t('connections:section.connectCta')}
                </IonButton>
              </div>
            </div>
          )}

          {/* Listing reach — chef-only default audience for published dishes */}
          {chef && activeProfile && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div>
                <h3 className="font-medium">{t('reach.title')}</h3>
                <p className="text-sm text-muted-foreground">{t('reach.description')}</p>
              </div>
              <ShareLevelPicker
                value={activeProfile.shareConnectionLevel ?? 3}
                onChange={(level) =>
                  updateReach.mutate({
                    profileId: activeProfile.id,
                    shareConnectionLevel: level,
                  })
                }
                disabled={updateReach.isPending}
                maxLevel={localeMaxShareConnectionLevel}
              />
            </div>
          )}

          {/* Feed preference — inbound audience for every user: whose listings
              show up in this user's home feed. Not locale-capped. */}
          {activeProfile && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div>
                <h3 className="font-medium">{t('seeReach.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('seeReach.description')}
                </p>
              </div>
              <ShareLevelPicker
                value={activeProfile.seeConnectionLevel ?? 3}
                onChange={(level) =>
                  updateSeeReach.mutate({
                    profileId: activeProfile.id,
                    seeConnectionLevel: level,
                  })
                }
                disabled={updateSeeReach.isPending}
                keyPrefix="seeReach"
              />
            </div>
          )}
        </div>
        </PullToRefresh>
      </IonContent>

      <ConnectFriendsModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
      />
      <ConnectionsListModal
        isOpen={showConnectionsModal}
        onClose={() => setShowConnectionsModal(false)}
      />
    </IonPage>
  );
};
